import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { RowInput } from 'jspdf-autotable';
import { POSModel } from '../../models/orders/order-list-model';
import orderServices from '../../services/orders/orderservice';
import { addGstSummaryTable } from '../../utils/gst-summary-pdf';
import { addPdfPageNumberFooter } from '../../utils/pdf-page-footer';

// Interfaces
interface CategorySummary {
  categoryId: number;
  categoryName: string;
  totalQuantity: number;
  totalGrossAmount: number;
  totalTaxableAmount: number;
  totalTaxAmount: number;
}

interface LineItem {
  challanNo: string;
  categoryId: number;
  categoryName: string;
  productId: number;
  productName: string;
  quantity: number;
  taxableAmount: number;
  taxAmount: number;
  grossAmount: number;
  hsnCode: string;
  gst?: number;
  createdDate?: string;
  ewayBillNo: string;
  vehicleNo: string;
}

export interface VesselDetail {
  productName: string;
  totalQuantity: number;
  vessels_5L: number;
  vessels_3L: number;
  vessels_2L: number;
}

interface ProductSummaryProps {
  categorySummaries: CategorySummary[];
  lineItems: LineItem[];
  posData?: POSModel | null;
  drivername?: string;
  vesselDetails?: VesselDetail[];
  fetchPOS?: boolean;
}

const formatDate = (dateString: string) => {
  const date = dateString ? new Date(dateString) : new Date();
  if (isNaN(date.getTime())) {
    return new Date().toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  }
  return date.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  });
};

export const generateProductSummaryPDF = async ({
  categorySummaries = [],
  lineItems = [],
  posData = null,
  drivername = "",
  vesselDetails = [],
  fetchPOS = true
}: ProductSummaryProps) => {
  // ✅ Filter out negative value products and categories
  const validLineItems = lineItems.filter(item => Number(item.grossAmount) >= 0);
  const validCategorySummaries = categorySummaries.filter(cat => Number(cat.totalGrossAmount) >= 0);

  // Fetch POS data if not provided
  let posAddressData = posData;
  if (!posAddressData && fetchPOS) {
    try {
      const response = await orderServices.getPOS();
      posAddressData = response[0] || null;
    } catch (error) {
      console.error("Error fetching POS data:", error);
      posAddressData = null;
    }
  }

  const vesselDetailsData = Array.isArray(vesselDetails) ? vesselDetails : [];
  
  const doc = new jsPDF('p', 'mm', 'a4');
  const challanNo = validLineItems.length > 0 && validLineItems[0].challanNo ? validLineItems[0].challanNo : "";
  const date = validLineItems.length > 0 && validLineItems[0].createdDate
    ? formatDate(validLineItems[0].createdDate)
    : "";

  // Set initial position
  let yPos = 15;

  const leftCol = 14;
  const rightCol = 110;
  
  // Font settings
  const fontFamily = 'helvetica';
  doc.setFont(fontFamily, 'normal');

  // Show header only if all values exist
  const shouldShowHeader = posAddressData && challanNo && drivername && date;

  if (shouldShowHeader) {
    // Add title
    doc.setFontSize(16);
    doc.setFont(fontFamily, 'bold');
    doc.text('Delivery Challan', leftCol, yPos);
    doc.setFont(fontFamily, 'normal');
    yPos += 10;

    // From section
    doc.setFontSize(11);
    doc.setFont(fontFamily, 'bold');
    doc.text('From:', leftCol, yPos);
    doc.setFont(fontFamily, 'normal');
    doc.text('Desiri Naturals Private Limited', leftCol, yPos + 6);

    if (posAddressData) {
      doc.text(posAddressData.address1 || '', leftCol, yPos + 12);
      if (posAddressData.address2) doc.text(posAddressData.address2, leftCol, yPos + 18);
      if (posAddressData.landmark) doc.text(posAddressData.landmark, leftCol, yPos + 24);
      doc.text(`${posAddressData.cityName}, ${posAddressData.stateName} - ${posAddressData.zipcode}`, leftCol, yPos + 24);
      doc.text('GST #: 29AAGCD5864J1Z4', leftCol, yPos + 30);
    }

    // To section
    doc.setFont(fontFamily, 'bold');
    doc.text('To:', rightCol, yPos);
    doc.setFont(fontFamily, 'normal');
    doc.text('Un Registered Persons (URP)/ Retail Customers', rightCol, yPos + 6);
    doc.text('Retail customers List as below', rightCol, yPos + 12);
    doc.text('List of Products: As mentioned below', rightCol, yPos + 18);
    yPos += posAddressData ? 40 : 24;

    // Purpose
    doc.setFont(fontFamily, 'bold');
    doc.text('Purpose of material movement:', leftCol, yPos);
    doc.setFont(fontFamily, 'normal');
    yPos += 6;
    doc.setFontSize(10);
    doc.text('1. Below list of materials are moved against the customer orders placed on Desiri Naturals Pvt Ltd through online.', leftCol, yPos, { maxWidth: 180 });
    yPos += 6;
    doc.text('2. Invoice will be generated after physical delivery to customer address mentioned below.', leftCol, yPos, { maxWidth: 180 });
    yPos += 10;

    // Challan info
    doc.setFontSize(11);
    doc.setFont(fontFamily, 'bold');
    const challanNoText = `Delivery Challan # : ${validLineItems[0]?.challanNo || ''}`;
    const ewayBillNoText = validLineItems[0]?.ewayBillNo ? `Eway Bill # : ${validLineItems[0].ewayBillNo}` : '';
    const ewayVehicleNo = validLineItems[0]?.ewayBillNo ? `Vehicle # : ${validLineItems[0].vehicleNo}` : '';
    doc.text(challanNoText, leftCol, yPos);
    doc.text(ewayBillNoText, 160, yPos, { align: 'right' }); 
    yPos += 7;
    doc.text(ewayVehicleNo, leftCol, yPos);
    doc.text(`Generated By : ${drivername || ''}`, 160, yPos, { align: 'right' });
    yPos += 7;
    doc.text(`Generated Date : ${date || ''}`, leftCol, yPos);
    yPos += 12;
  }

  // Add product details title
  doc.setFontSize(14);
  doc.setFont(fontFamily, 'bold');
  doc.text('Product Details', leftCol, yPos);
  doc.setFont(fontFamily, 'normal');
  yPos += 8;

  if (validLineItems.length > 0) {
    // Group line items by category
     const groupedItems: {[key: string]: LineItem[]} = {};
     validLineItems.forEach(item => {
      if (!groupedItems[item.categoryName]) {
        groupedItems[item.categoryName] = [];
      }
      groupedItems[item.categoryName].push(item);
    });

    // Prepare data for autotable
    const body: RowInput[] = [];
    
    Object.entries(groupedItems).forEach(([categoryName, items]) => {
      // Add category header row
      body.push([
        { 
          content: categoryName, 
          colSpan: 6, 
          styles: { 
            fontStyle: 'bold', 
            textColor: [0, 0, 0]
          }
        }
      ]);

      // Add product rows
      items.forEach(item => {
        body.push([
          item.productName,
          item.quantity.toString(),
          item.hsnCode,
          item.taxableAmount.toFixed(2),
          item.taxAmount.toFixed(2),
          item.grossAmount.toFixed(2)
        ]);
      });

      // Add category footer row
      const categorySummary = validCategorySummaries.find(c => c.categoryName === categoryName);
      body.push([
        { 
          content: `${categoryName} Total: ${categorySummary?.totalQuantity || 0} items (Rs.${(categorySummary?.totalGrossAmount || 0).toFixed(2)})`, 
          colSpan: 6, 
          styles: { 
            fontStyle: 'bold',
            textColor: [0, 0, 0]
          }
        }
      ]);
    });

    autoTable(doc, {
      head: [['Product', 'Quantity', 'HSN', 'Price Value(Rs)', 'Tax Value(Rs)', 'Total Amount(Rs)']],
      body: body,
      startY: yPos,
      headStyles: {
        fontStyle: 'bold'
      },
      columnStyles: {
        0: { cellWidth: 'auto', halign: 'left' },
        1: { cellWidth: 'auto', halign: 'center' },
        2: { cellWidth: 'auto', halign: 'center' },
        3: { cellWidth: 'auto', halign: 'center' },
        4: { cellWidth: 'auto', halign: 'center' },
        5: { cellWidth: 'auto', halign: 'center' }
      },
      styles: {
        cellPadding: 3,
        fontSize: 10,
        valign: 'middle',
        font: 'helvetica'
      },
      didDrawPage: (data) => {
        yPos = data.cursor?.y || yPos + 10;
      }
    });
  } else {
    doc.text('No products found in the challan.', leftCol, yPos);
    yPos += 10;
  }

  // Add summary totals
  yPos += 10;
  doc.setFontSize(14);
  doc.setFont(fontFamily, 'bold');
  doc.text('Summary Totals', leftCol, yPos);
  doc.setFont(fontFamily, 'normal');
  yPos += 8;
  
  // Calculate totals
  const priceValueTotal = validCategorySummaries.reduce((sum, category) => sum + (category.totalTaxableAmount || 0), 0);
  const taxValueTotal = validCategorySummaries.reduce((sum, category) => sum + (category.totalTaxAmount || 0), 0);
  const grandTotal = validCategorySummaries.reduce((sum, category) => sum + (category.totalGrossAmount || 0), 0);

  // Prepare summary table data
  const summaryBody: RowInput[] = validCategorySummaries.map(category => [
    category.categoryName,
    category.totalTaxableAmount.toFixed(2),
    category.totalTaxAmount.toFixed(2),
    category.totalGrossAmount.toFixed(2)
  ]);

  // Add total row
  summaryBody.push([
    { 
      content: 'Total',
      styles: { 
        fontStyle: 'bold',
        textColor: [0, 0, 255]
      }
    },
    { 
      content: priceValueTotal.toFixed(2),
      styles: { 
        fontStyle: 'bold',
        textColor: [0, 0, 255]
      }
    },
    { 
      content: taxValueTotal.toFixed(2),
      styles: { 
        fontStyle: 'bold',
        textColor: [0, 0, 255]
      }
    },
    { 
      content: grandTotal.toFixed(2),
      styles: { 
        fontStyle: 'bold',
        textColor: [0, 0, 255]
      }
    }
  ]);

  autoTable(doc, {
    head: [['Category', 'Total Price Value (Rs)', 'Total Tax Value (Rs)', 'Total Amount (Rs)']],
    body: summaryBody,
    startY: yPos,
    headStyles: {
      textColor: 255,
      fontStyle: 'bold'
    },
    columnStyles: {
      0: { cellWidth: 'auto', halign: 'left' },
      1: { cellWidth: 'auto', halign: 'center' },
      2: { cellWidth: 'auto', halign: 'center' },
      3: { cellWidth: 'auto', halign: 'center' }
    },
    bodyStyles: {
      [summaryBody.length - 1]: { fontStyle: 'bold', textColor: [0, 0, 255] }
    },
    styles: {
      cellPadding: 3,
      fontSize: 10,
      valign: 'middle'
    },
    didDrawPage: (data) => {
      yPos = data.cursor?.y || yPos + 10;
    }
  });

  yPos = addGstSummaryTable(doc, validLineItems, yPos, leftCol);

  // Add vessel details section if data exists
  if (vesselDetailsData.length > 0) {
    yPos += 15;
    doc.setFontSize(14);
    doc.setFont(fontFamily, 'bold');
    doc.text('Vessel Details', leftCol, yPos);
    doc.setFont(fontFamily, 'normal');
    yPos += 8;

    const vesselBody: RowInput[] = vesselDetailsData.map(vessel => [
      vessel.productName,
      vessel.totalQuantity.toString(),
      vessel.vessels_5L.toString(),
      vessel.vessels_3L.toString(),
      vessel.vessels_2L.toString(),
    ]);

    autoTable(doc, {
      head: [['Name', 'Total Qty', '5L Vessels', '3L Vessels', '2L Vessels']],
      body: vesselBody,
      startY: yPos,
      headStyles: {
        textColor: 255,
        fontStyle: 'bold'
      },
      columnStyles: {
        0: { cellWidth: 'auto', halign: 'left' },
        1: { cellWidth: 'auto', halign: 'center' },
        2: { cellWidth: 'auto', halign: 'center' },
        3: { cellWidth: 'auto', halign: 'center' },
        4: { cellWidth: 'auto', halign: 'center' },
        5: { cellWidth: 'auto', halign: 'center' },
        6: { cellWidth: 'auto', halign: 'center' }
      },
      styles: {
        cellPadding: 3,
        fontSize: 10,
        valign: 'middle'
      },
      didDrawPage: (data) => {
        yPos = data.cursor?.y || yPos + 10;
      }
    });
  }

  // Save the PDF
  addPdfPageNumberFooter(doc);
  doc.save(`Delivery-Challan-${challanNo || 'Unknown'}.pdf`);
};
